import { BarsArrowDownIcon, BarsArrowUpIcon } from "@heroicons/react/24/outline";
import { flexRender, Table } from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { cn } from "utils/ui";

interface DataTableProps<T> {
  table: Table<T>;
  type: "offer" | "task" | "request";
  shownDetailDataId?: string;
  /**
   * Keep the column header visible while the nearest scrolling ancestor scrolls. That ancestor then
   * owns both scroll axes: this wrapper must not create a scroll container, or the header sticks to it.
   */
  stickyHeader?: boolean;
}

export function DataTable<T extends { id: string }>({
  table,
  type,
  shownDetailDataId,
  stickyHeader = false,
}: DataTableProps<T>) {
  return (
    <div
      className={cn(
        "block rounded-md border border-slate-200 shadow dark:border-slate-800",
        stickyHeader ? "w-fit min-w-full overflow-clip" : "max-w-full overflow-y-hidden overflow-x-scroll"
      )}
      data-test={`${type}-table-container`}
    >
      <table className="w-full" data-test={`${type}-table`}>
        <DataTableHeader table={table} type={type} stickyHeader={stickyHeader} />
        <DataTableBody table={table} type={type} shownDetailDataId={shownDetailDataId} />
      </table>
    </div>
  );
}

export function DataTableHeader<T extends { id: string }>({ table, type, stickyHeader = false }: DataTableProps<T>) {
  return (
    <thead className="text-color-primary bg-color-primary" data-test={`${type}-table-head`}>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            return (
              <th
                key={header.id}
                colSpan={header.colSpan}
                className={cn({ "bg-color-primary sticky top-0 z-10": stickyHeader })}
              >
                {header.isPlaceholder ? null : (
                  <div
                    className={header.column.getCanSort() ? "cursor-pointer select-none" : ""}
                    onClick={header.column.getToggleSortingHandler()}
                    title={
                      header.column.getCanSort()
                        ? header.column.getNextSortingOrder() === "asc"
                          ? "Sort ascending"
                          : header.column.getNextSortingOrder() === "desc"
                            ? "Sort descending"
                            : "Clear sort"
                        : undefined
                    }
                  >
                    <div className="flex items-center px-5 py-4 text-sm font-semibold">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: <BarsArrowUpIcon data-test={`col-${header.id}-ascending-icon`} className="ml-3 size-4" />,
                        desc: (
                          <BarsArrowDownIcon data-test={`col-${header.id}-descending-icon`} className="ml-3 size-4" />
                        ),
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  </div>
                )}
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
}

export function DataTableBody<T extends { id: string }>({ table, type, shownDetailDataId }: DataTableProps<T>) {
  const isSideBarOpen = !!shownDetailDataId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return (
    <tbody
      className="divide-y divide-gray-200 bg-white text-sm dark:divide-slate-600 dark:bg-slate-800"
      data-test={`${type}-table-body`}
    >
      {table.getRowModel().rows.map((row) => {
        const isSelected = row.original.id === shownDetailDataId;
        return (
          <tr
            key={row.id}
            data-test={`row-${type}-${row.id}`}
            onClick={() => navigate(`./${row.original.id}?${searchParams.toString()}`)}
            className={cn("cursor-pointer", isSelected ? "bg-blue-50 dark:bg-slate-600/30" : "")}
          >
            {row.getVisibleCells().map((cell) => {
              return (
                <td
                  key={cell.id}
                  className={cn("p-5", isSideBarOpen ? "2xl:table-cell" : "xl:table-cell")}
                  style={{
                    width: cell.column.getSize(),
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  );
}
